// ---- Structure decoration kit (concept-B timber + stone) ----
// Reusable primitives for freestanding-house character that is NOT the shell:
//   - stoneCourses — irregular block courses on a raised foundation
//   - stoneSteps   — multi-step stair on a door face
//   - timberFraming — wall-plate beams + corner posts + diagonal braces under eaves
//   - doorLeaf     — plank door slab + hardware (kept, walk-through still clear via
//                    doorway's open collider gap; leaf is thin decoration only)
//
// All pure StructureBuildCtx callers — no collision unless noted. Foundations and
// stairs use solid/box; timber uses soft UNIT_BOX (via box) so joins read rounded
// like concept B. Seeded size jitter keeps courses from looking tiled.
//
// Call sites: doc.ts `base` / `mass` (timber package) / openings.ts doorway.

import type { StructureBuildCtx } from "./define.ts";
import { shade } from "../color.ts";

export type Side = "north" | "south" | "east" | "west";

/** Deterministic 0..1 from a small integer key (no full RNG needed). */
function hash01(n: number): number {
  const x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b) >>> 0;
  return (x & 0xffff) / 0xffff;
}

const solidOf = (c: StructureBuildCtx) =>
  (x: number, y: number, z: number, w: number, h: number, d: number, col: number) => {
    if (c.solid) c.solid(x, y, z, w, h, d, col);
    else c.box(x, y, z, w, h, d, col);
  };

// =============================================================================
// Stone courses — raised foundation veneer (concept B's chunky block base)
// =============================================================================

export type StoneCoursesOpts = {
  /** Bottom of the course band (world y). */
  y0: number;
  /** Total band height. */
  height: number;
  /** Number of horizontal courses (default 2). */
  courses?: number;
  /** How far courses project past the rect face (default 0.28). */
  depth?: number;
  color: number;
  colorDk?: number;
  /**
   * Optional gap on one face so stairs/doorway don't fight the veneer.
   * `at`/`half` are along the face run axis (local x for N/S, z for E/W).
   */
  gap?: { side: Side; at: number; half: number };
  /** Seed for block size jitter (default 1). */
  seed?: number;
};

/**
 * Irregular rectangular stone blocks stacked as courses around a footprint.
 * Purely decorative (no collider — the solid base pad already carries mass).
 * Gaps around a door face so stairs/opening read clean.
 */
export function stoneCourses(
  c: StructureBuildCtx,
  minX: number, maxX: number, minZ: number, maxZ: number,
  opts: StoneCoursesOpts,
): void {
  const nCourses = Math.max(1, Math.round(opts.courses ?? 2));
  const depth = opts.depth ?? 0.28;
  const color = opts.color;
  const colorDk = opts.colorDk ?? shade(color, -0.14);
  const seed = opts.seed ?? 1;
  const courseH = opts.height / nCourses;
  const solid = solidOf(c);

  type Face = {
    side: Side;
    nx: 0 | 1 | -1;
    nz: 0 | 1 | -1;
    // place(alongMid, yMid, runW, hy) in face-local terms
    place: (along: number, y: number, runW: number, hy: number, d: number, col: number) => void;
    lo: number;
    hi: number;
  };

  const faces: Face[] = [
    {
      side: "north", nx: 0, nz: -1,
      lo: minX, hi: maxX,
      place: (a, y, rw, hy, d, col) => solid(a, y, minZ - depth / 2, rw, hy, d, col),
    },
    {
      side: "south", nx: 0, nz: 1,
      lo: minX, hi: maxX,
      place: (a, y, rw, hy, d, col) => solid(a, y, maxZ + depth / 2, rw, hy, d, col),
    },
    {
      side: "west", nx: -1, nz: 0,
      lo: minZ, hi: maxZ,
      place: (a, y, rw, hy, d, col) => solid(minX - depth / 2, y, a, d, hy, rw, col),
    },
    {
      side: "east", nx: 1, nz: 0,
      lo: minZ, hi: maxZ,
      place: (a, y, rw, hy, d, col) => solid(maxX + depth / 2, y, a, d, hy, rw, col),
    },
  ];

  // Shell-tag per façade so foundation courses wall-fade with the camera-facing
  // wall (keep() left them solid while the upper wall melted — see cutaway).
  let blockId = seed * 17;
  for (const face of faces) {
    c.shell(face.nx, face.nz, () => {
      for (let ci = 0; ci < nCourses; ci++) {
        const yMid = opts.y0 + (ci + 0.5) * courseH;
        // Alternate course starts so vertical joints stagger (running bond).
        const offset = (ci % 2) * 0.45;
        let cursor = face.lo - offset * 0.3;
        // Nominal block run length with jitter.
        while (cursor < face.hi - 0.05) {
          blockId++;
          const j = hash01(blockId);
          const target = 0.85 + j * 0.55; // ~0.85..1.4
          let a1 = Math.min(cursor + target, face.hi);
          // Last short scrap merges into previous when tiny.
          if (face.hi - a1 < 0.35 && a1 < face.hi) a1 = face.hi;
          const a0 = Math.max(cursor, face.lo);
          if (a1 - a0 < 0.2) { cursor = a1; continue; }

          // Door/stair gap — skip or split.
          if (opts.gap && opts.gap.side === face.side) {
            const g0 = opts.gap.at - opts.gap.half;
            const g1 = opts.gap.at + opts.gap.half;
            // Fully inside gap → skip.
            if (a0 >= g0 && a1 <= g1) { cursor = a1; continue; }
            // Overlaps → emit left and/or right remnant.
            const segs: [number, number][] = [];
            if (a0 < g0) segs.push([a0, Math.min(a1, g0)]);
            if (a1 > g1) segs.push([Math.max(a0, g1), a1]);
            for (const [s0, s1] of segs) {
              if (s1 - s0 < 0.18) continue;
              emitBlock(face, s0, s1, yMid, courseH, depth, color, colorDk, blockId, solid);
              blockId++;
            }
            cursor = a1;
            continue;
          }

          emitBlock(face, a0, a1, yMid, courseH, depth, color, colorDk, blockId, solid);
          cursor = a1;
        }
      }
    });
  }

  // Corner quoins — straddle two façades; fade if either faces the camera.
  const q = depth * 1.15;
  const qRun = Math.min(0.7, (maxX - minX) * 0.12, (maxZ - minZ) * 0.12);
  for (const [cx, cz, sx, sz] of [
    [minX, minZ, -1, -1], [maxX, minZ, 1, -1],
    [minX, maxZ, -1, 1], [maxX, maxZ, 1, 1],
  ] as const) {
    c.shellEither(
      { nx: sx, nz: 0 },
      { nx: 0, nz: sz },
      () => {
        for (let ci = 0; ci < nCourses; ci++) {
          const yMid = opts.y0 + (ci + 0.5) * courseH;
          const col = (ci + Math.round(cx + cz)) % 2 === 0 ? color : colorDk;
          // Two half-quoins on each axis so corners read solid from both sides.
          solid(cx + sx * q / 2, yMid, cz, q, courseH * 0.92, qRun * 2, col);
          solid(cx, yMid, cz + sz * q / 2, qRun * 2, courseH * 0.92, q, col);
        }
      },
    );
  }
}

function emitBlock(
  face: { place: (a: number, y: number, rw: number, hy: number, d: number, col: number) => void },
  a0: number, a1: number,
  yMid: number, courseH: number, depth: number,
  color: number, colorDk: number, id: number,
  _solid: ReturnType<typeof solidOf>,
): void {
  const mid = (a0 + a1) / 2;
  const run = a1 - a0;
  // Slight height/depth jitter so courses don't look stamped.
  const hj = 0.9 + hash01(id * 3) * 0.12;
  const dj = depth * (0.92 + hash01(id * 5) * 0.16);
  const col = hash01(id * 7) > 0.55 ? colorDk : color;
  face.place(mid, yMid, run - 0.04, courseH * hj, dj, col);
}

// =============================================================================
// Stone steps — multi-step stair on a foundation face (concept B entry)
// =============================================================================

export type StoneStepsOpts = {
  steps?: number;       // default 3
  width: number;        // along the face
  totalDepth: number;   // how far the bottom step projects
  /** Top step surface y (door sill / foundation top). */
  topY: number;
  /** Ground / lowest step bottom (default 0 when topY > 0 else topY - rise). */
  bottomY?: number;
  color: number;
  colorDk?: number;
};

/**
 * Cascading stone slabs climbing to a door. Bottom step widest/deepest;
 * each step up is shorter in depth. Kept (never wall-fades). No walkable
 * surface — the solid base + door sill handle footing; these are visual
 * (players still step via the underlying terrain + pad). If you need a real
 * climb onto a tall pad, raise the pad and keep topY ≈ pad top.
 */
export function stoneSteps(
  c: StructureBuildCtx,
  side: Side,
  faceCoord: number,
  along: number,
  opts: StoneStepsOpts,
): void {
  const n = Math.max(1, Math.round(opts.steps ?? 3));
  const color = opts.color;
  const colorDk = opts.colorDk ?? shade(color, -0.12);
  const bottomY = opts.bottomY ?? Math.min(0, opts.topY - 0.9);
  const rise = Math.max(0.15, opts.topY - bottomY);
  const stepH = rise / n;
  const solid = solidOf(c);

  c.keep(() => {
    for (let i = 0; i < n; i++) {
      // i=0 is the bottom (outermost) step.
      const t = (i + 1) / n; // 0→1 toward the door
      const depth = opts.totalDepth * (1 - i / n) * (n === 1 ? 1 : 0.92);
      // Each step's TOP sits at bottomY + (i+1)*stepH; box centered in height.
      const top = bottomY + (i + 1) * stepH;
      const hy = stepH * 0.95 + 0.04; // slight overlap kills z-gaps
      const yMid = top - hy / 2;
      const col = i % 2 === 0 ? color : colorDk;
      // Step width shrinks a hair toward the door (softer silhouette).
      const w = opts.width * (1 - i * 0.04);

      if (side === "west") {
        const x = faceCoord - depth / 2;
        solid(x, yMid, along, depth, hy, w, col);
      } else if (side === "east") {
        const x = faceCoord + depth / 2;
        solid(x, yMid, along, depth, hy, w, col);
      } else if (side === "north") {
        const z = faceCoord - depth / 2;
        solid(along, yMid, z, w, hy, depth, col);
      } else {
        const z = faceCoord + depth / 2;
        solid(along, yMid, z, w, hy, depth, col);
      }
    }
  });
}

// =============================================================================
// Knee braces — diagonal timber (ALWAYS a rod, never stepped boxes)
// =============================================================================

/**
 * One knee brace as a consolidatable rod between two world points.
 * **Do not** approximate diagonals with stepped boxes — that walked outward
 * past corners and produced freefloating cubes (session bug).
 *
 * Returns false if the segment is degenerate (too short).
 */
export function kneeBrace(
  c: StructureBuildCtx,
  x0: number, y0: number, z0: number,
  x1: number, y1: number, z1: number,
  radius: number,
  color: number,
): boolean {
  const len = Math.hypot(x1 - x0, y1 - y0, z1 - z0);
  if (len < 0.08) return false;
  c.rod(x0, y0, z0, x1, y1, z1, radius, color);
  return true;
}

/**
 * Pair of knee braces on one wall face: from near each corner, UP-INWARD to
 * the wall plate (never outward past lo/hi — that was the freefloat bug).
 *
 * `facePoint(along, y)` returns world (x,y,z) on the proud face plane.
 */
export function kneeBracesOnFace(
  c: StructureBuildCtx,
  face: { lo: number; hi: number },
  facePoint: (along: number, y: number) => [number, number, number],
  opts: {
    postBaseY: number;
    plateY: number;
    wallH: number;
    beam: number;
    color: number;
  },
): number {
  const { postBaseY, plateY, wallH, beam, color } = opts;
  const span = face.hi - face.lo;
  if (span < 0.8) return 0;
  const braceRun = Math.min(2.2, span * 0.32);
  const r = beam * 0.38;
  const yLow = postBaseY + Math.min(1.4, wallH * 0.22);
  const yHigh = plateY - beam * 0.15;
  const mid = (face.lo + face.hi) / 2;
  let n = 0;
  for (const fromLo of [true, false]) {
    const aLow = fromLo ? face.lo + beam * 0.55 : face.hi - beam * 0.55;
    const aHigh = fromLo ? aLow + braceRun : aLow - braceRun;
    // Clamp tip toward center — NEVER past lo/hi outward.
    const aTip = fromLo ? Math.min(aHigh, mid) : Math.max(aHigh, mid);
    if (aTip < face.lo - 0.01 || aTip > face.hi + 0.01) continue;
    if (aLow < face.lo - 0.01 || aLow > face.hi + 0.01) continue;
    const [x0, y0, z0] = facePoint(aLow, yLow);
    const [x1, y1, z1] = facePoint(aTip, yHigh);
    if (kneeBrace(c, x0, y0, z0, x1, y1, z1, r, color)) n++;
  }
  return n;
}

// =============================================================================
// Timber framing — wall plates + corner posts + diagonal braces (concept B)
// =============================================================================

export type TimberFramingOpts = {
  color: number;
  /** Horizontal wall-plate beam under eaves on every face (default true). */
  plate?: boolean;
  /** Vertical corner posts foundation→plate (default true). */
  posts?: boolean;
  /** Diagonal braces from mid-post up to plate (default true). */
  braces?: boolean;
  /** Beam cross-section (default 0.32). */
  beam?: number;
  /** How far timber sits proud of the wall face (default 0.14). */
  proud?: number;
  /**
   * Optional door: heavy external posts + lintel on that face (in addition to
   * the structural doorway frame). Matches concept B's chunky entry surround.
   */
  door?: { side: Side; at: number; w: number; h: number };
  /** y where posts start (top of foundation course band; default baseY). */
  postBaseY?: number;
};

/**
 * Storybook timber package for a rectangular mass: wall-plate under the eaves,
 * corner posts, diagonal braces, optional heavy door surround. Soft box prims
 * (rounded) so joins read organic like concept B. Shell-tagged per face so
 * trim wall-fades with the façade; corner posts use shellEither (two faces).
 */
export function timberFraming(
  c: StructureBuildCtx,
  minX: number, maxX: number, minZ: number, maxZ: number,
  baseY: number, wallH: number,
  opts: TimberFramingOpts,
): void {
  const color = opts.color;
  const colorLt = shade(color, 0.1);
  const beam = opts.beam ?? 0.32;
  const proud = opts.proud ?? 0.14;
  const doPlate = opts.plate !== false;
  const doPosts = opts.posts !== false;
  const doBraces = opts.braces !== false;
  const postBase = opts.postBaseY ?? baseY;
  const plateY = baseY + wallH - beam * 0.55;
  const postH = plateY - postBase + beam * 0.3;

  type Face = {
    side: Side;
    nx: 0 | 1 | -1;
    nz: 0 | 1 | -1;
    /** Place a box with run along the face: (alongMid, yMid, runW, hy, depth). */
    place: (along: number, y: number, runW: number, hy: number, depth: number, col: number) => void;
    /** Outward face coordinate. */
    face: number;
    lo: number;
    hi: number;
  };

  const faces: Face[] = [
    {
      side: "north", nx: 0, nz: -1, face: minZ, lo: minX, hi: maxX,
      place: (a, y, rw, hy, d, col) => c.box(a, y, minZ - proud - d / 2, rw, hy, d, col),
    },
    {
      side: "south", nx: 0, nz: 1, face: maxZ, lo: minX, hi: maxX,
      place: (a, y, rw, hy, d, col) => c.box(a, y, maxZ + proud + d / 2, rw, hy, d, col),
    },
    {
      side: "west", nx: -1, nz: 0, face: minX, lo: minZ, hi: maxZ,
      place: (a, y, rw, hy, d, col) => c.box(minX - proud - d / 2, y, a, d, hy, rw, col),
    },
    {
      side: "east", nx: 1, nz: 0, face: maxX, lo: minZ, hi: maxZ,
      place: (a, y, rw, hy, d, col) => c.box(maxX + proud + d / 2, y, a, d, hy, rw, col),
    },
  ];

  // ---- Corner posts (straddle two façades — fade if either faces the camera) ----
  if (doPosts) {
    for (const [px, pz, nxA, nzA, nxB, nzB] of [
      [minX - proud, minZ - proud, -1, 0, 0, -1],
      [maxX + proud, minZ - proud, 1, 0, 0, -1],
      [minX - proud, maxZ + proud, -1, 0, 0, 1],
      [maxX + proud, maxZ + proud, 1, 0, 0, 1],
    ] as const) {
      c.shellEither(
        { nx: nxA, nz: nzA },
        { nx: nxB, nz: nzB },
        () => {
          // Slightly oversized so they read as roundish timber posts.
          c.box(px, postBase + postH / 2, pz, beam * 1.05, postH, beam * 1.05, color);
          // Cap block under the plate.
          c.box(px, plateY + beam * 0.15, pz, beam * 1.2, beam * 0.45, beam * 1.2, colorLt);
        },
      );
    }
  }

  // Outward offset for face-mounted rods (depth mid of a plate beam).
  const faceOut = (f: Face, depth: number): { x: number; z: number } => {
    const d = proud + depth / 2;
    if (f.side === "north") return { x: 0, z: minZ - d };
    if (f.side === "south") return { x: 0, z: maxZ + d };
    if (f.side === "west") return { x: minX - d, z: 0 };
    return { x: maxX + d, z: 0 };
  };
  const facePoint = (f: Face, along: number, y: number, depth: number): [number, number, number] => {
    const o = faceOut(f, depth);
    if (f.side === "north" || f.side === "south") return [along, y, o.z];
    return [o.x, y, along];
  };

  // ---- Wall plates + braces per face ----
  for (const f of faces) {
    c.shell(f.nx, f.nz, () => {
      if (doPlate) {
        // Full-run plate under the eave; corners overlap posts.
        const run = f.hi - f.lo + beam * 0.8;
        const mid = (f.lo + f.hi) / 2;
        f.place(mid, plateY, run, beam, beam * 0.85, color);
      }

      if (doBraces) {
        kneeBracesOnFace(
          c,
          { lo: f.lo, hi: f.hi },
          (along, y) => facePoint(f, along, y, beam * 0.7),
          { postBaseY: postBase, plateY, wallH, beam, color },
        );
      }

      // Heavy door surround on this face.
      if (opts.door && opts.door.side === f.side) {
        const { at, w, h } = opts.door;
        const half = w / 2;
        const postW = beam * 1.15;
        const postD = beam * 1.25;
        // Jambs — full door height, proud of the wall.
        f.place(at - half - postW / 2, baseY + h / 2, postW, h + beam * 0.2, postD, color);
        f.place(at + half + postW / 2, baseY + h / 2, postW, h + beam * 0.2, postD, color);
        // Lintel beam.
        f.place(at, baseY + h + beam * 0.35, w + postW * 2.2, beam * 0.9, postD * 1.05, colorLt);
        // Threshold plank.
        f.place(at, baseY + beam * 0.2, w + postW, beam * 0.35, postD * 0.9, color);
      }
    });
  }

  // Mid rail on the long eaves under the brace junction — the horizontal timber
  // concept B shows connecting posts to the roof plate.
  if (doBraces) {
    const longZ = (maxZ - minZ) >= (maxX - minX);
    const midY = baseY + wallH * 0.55;
    const faces2 = longZ
      ? [faces.find((x) => x.side === "west")!, faces.find((x) => x.side === "east")!]
      : [faces.find((x) => x.side === "north")!, faces.find((x) => x.side === "south")!];
    for (const f of faces2) {
      c.shell(f.nx, f.nz, () => {
        const run = f.hi - f.lo + beam * 0.4;
        f.place((f.lo + f.hi) / 2, midY, run, beam * 0.7, beam * 0.7, color);
      });
    }
  }
}

// =============================================================================
// Door leaf — visual plank door inside an opening (no collider)
// =============================================================================

export type DoorLeafOpts = {
  /** Opening center along the wall run. */
  at: number;
  half: number;
  h: number;
  baseY: number;
  /** Wall face coordinate on the normal axis. */
  faceCoord: number;
  /** Outward normal. */
  nx: 0 | 1 | -1;
  nz: 0 | 1 | -1;
  axis: "x" | "z";
  color: number;
  /** Iron hardware color (default dark). */
  iron?: number;
};

/**
 * Thin plank door set slightly recessed in the opening. No collider — the
 * doorway flanks already leave a walk-through gap. Kept so it never wall-fades
 * away when viewed from inside.
 */
export function doorLeaf(c: StructureBuildCtx, opts: DoorLeafOpts): void {
  const { at, half, h, baseY, faceCoord, nx, nz, axis, color } = opts;
  const iron = opts.iron ?? 0x3a3a42;
  const faceDir = (nx || nz) as 1 | -1;
  // Recessed just inside the outer face so the frame reads proud around it.
  const doorFace = faceCoord - faceDir * 0.08;
  const thick = 0.12;
  const yMid = baseY + h / 2;
  const w = half * 2 - 0.12;

  const place = (a: number, y: number, runW: number, hy: number, d: number, col: number) =>
    axis === "x"
      ? c.box(a, y, doorFace, runW, hy, d, col)
      : c.box(doorFace, y, a, d, hy, runW, col);

  c.keep(() => {
    // Main slab.
    place(at, yMid, w, h - 0.08, thick, color);
    // Vertical plank grooves (darker strips).
    const nPlanks = 4;
    for (let i = 1; i < nPlanks; i++) {
      const a = at - half + 0.06 + (w * i) / nPlanks;
      place(a, yMid, 0.04, h - 0.16, thick + 0.02, shade(color, -0.1));
    }
    // Horizontal battens.
    place(at, baseY + h * 0.28, w - 0.1, 0.1, thick + 0.03, shade(color, -0.08));
    place(at, baseY + h * 0.72, w - 0.1, 0.1, thick + 0.03, shade(color, -0.08));
    // Handle (toward the right from outside).
    const handleA = at + half * 0.55;
    const handleY = baseY + h * 0.48;
    place(handleA, handleY, 0.1, 0.28, thick + 0.1, iron);
    // Hinge nubs on the left.
    for (const hy of [0.25, 0.55, 0.8]) {
      place(at - half + 0.08, baseY + h * hy, 0.12, 0.12, thick + 0.08, iron);
    }
  });
}
